import {Grid, SnackbarCloseReason} from '@mui/material';
import AurSelectDropdown from '../../common/aur-select-dropdown/AurSelectDropdown';
import AurChipList, {AurChipConfig} from '../../common/aur-chip/AurChipList';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {t} from 'i18next';
import AurPopup from '../../common/aur-popup/AurPopup';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  getIndexByName,
  getIndexHistory,
  reRunBuildHandler,
  retryNlpDocuments,
  retryOverlay,
} from '../../../api/EcaServices';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  setIndexingBannerData,
  setReRunPopup,
} from '../../../redux/slices/indexingManagementSlice';
import {
  setRebuildTrigger,
  setShowToast,
} from '../../../redux/slices/indexingSlice';
import AurToast from '../../common/aur-toast/AurToast';
import {
  datasetName,
  EcaIndexStatus,
  getIndexingBannerFromStatusList,
  IndexesType,
  indexingStatusKey,
  isCompletedWithErrorStatus,
  isDeleteDocumentsEvent,
  ISelectedItems,
  normalizeDeleteDocumentsEvent,
} from '../model';
import AurDataGridNoRowsOverlay from '../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import {
  INDEXING_HISTORY,
  INDEXING_PARENT,
  INDEXING_SUB_INFO,
  INDEXING_SUBINDEX_CHILD,
  INDEXING_SUBINDEX_EVENTS,
  INDEXING_SUBINDEX_HEIGHT,
} from '../../../constants/constants';
import {generateRandomNumber} from '../../../utils/utils';
import {FilterSelection} from '../../common/aur-chip/AurChip';
import {getIndexColumns} from './IndexingColumns';
import AurDataGridPro from '../../common/aur-data-grid-pro/AurDataGridPro';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {GridRowHeightParams} from '@mui/x-data-grid';
import {ISelectItem} from '../../common/aur-select-dropdown/types';

const sortByName = <T extends {name?: string}>(items: T[]): T[] =>
  [...items].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

const buildTypeList = () => {
  return sortByName([
    {
      id: 0,
      name: t('indexes.overlay'),
    },
    {
      id: 1,
      name: t('indexes.newExportSet'),
    },
    {
      id: 2,
      name: t('indexes.overlayMediaApcUpdate'),
    },
    {
      id: 3,
      name: t('indexes.overlayProjectApcUpdate'),
    },
    {
      id: 4,
      name: t('indexes.deleteDocuments'),
    },
  ]);
};

const statusList = () => {
  return sortByName([
    {
      id: 0,
      name: t('indexes.inProgress'),
    },
    {
      id: 1,
      name: t('indexes.completed'),
    },
    {
      id: 2,
      name: t('indexes.completedWithError'),
    },
    {
      id: 3,
      name: t('indexes.failed'),
    },
    {
      id: 4,
      name: t('indexes.queued'),
    },
    {
      id: 5,
      name: t('indexes.pending'),
    },
    {
      id: 6,
      name: t('indexes.new'),
    },
    {
      id: 7,
      name: t('indexes.retrying'),
    },
  ]);
};

const ReRunPopContent = () => {
  return (
    <div className="typo-text body2_size rerun-popup-text-color">
      {' '}
      {t(`indexes.reRunPopup.content`)}
    </div>
  );
};

const AurDataGridLoadingOverlay: React.FC = () => {
  const indexDetails = useSelector(
    (state: RootState) => state.indexes.indexData
  );

  const isCustomOrNLP =
    indexDetails.ele.type === IndexesType.custom ||
    indexDetails?.ele?.type === IndexesType.nlp;

  const custMessage = isCustomOrNLP
    ? t('common.noIndexingEvents')
    : t('common.noResultFound');

  return <AurDataGridNoRowsOverlay message={custMessage} />;
};

const ROW_HEIGHT = 64;

const IndexingFilter = ({
  skipApiBannerValidation = false,
}: {
  skipApiBannerValidation?: boolean;
}) => {
  const {trackGAEvent} = useAnalytics();
  const dispatch = useDispatch();
  const [clearAllModal, setClearAllModal] = useState<boolean>(false);
  const [hasFilterValueChanged, setHasFilterValueChanged] = useState(true);
  const [rows, setRows] = useState<EcaIndexStatus[]>([]);
  const [isHistoryDetailLoading, setIsHistoryDetailLoading] = useState(false);
  const [initiatedList, setInitiatedList] = useState<ISelectItem[]>([]);
  const [visibleReRunPopup, setVisibleReRunPopup] = useState<boolean>(true);
  const [openToast, setOpenToast] = useState<boolean>(false);

  const showToastMsg = useSelector(
    (state: RootState) => state.indexes.showToast
  );
  const indexDetails = useSelector(
    (state: RootState) => state.indexes.indexData
  );
  const showReRunFailedPopup = useSelector(
    (state: RootState) => state.indexesBanner.showReRunFailedPopup
  );
  const {
    showpopup: showReRunPopup,
    indexId: reRunIndexId,
    ecaNlpingestionId,
    exportId,
    mediaId,
    dataSetName,
    datFilePath,
    overlayRetryData,
  } = showReRunFailedPopup;

  const [chipConfigs, setChipConfigs] = useState<AurChipConfig[]>([
    {
      id: 1,
      filterSelection: [],
      filterName: t('filters.buildType'),
      showRemove: true,
      onRemove: () => onRemoveFilter(1),
    },
    {
      id: 2,
      filterSelection: [],
      filterName: t('filters.status'),
      showRemove: true,
      onRemove: () => onRemoveFilter(2),
    },
    {
      id: 3,
      filterSelection: [],
      filterName: t('filters.initiatedBy'),
      showRemove: true,
      onRemove: () => onRemoveFilter(3),
    },
  ]);

  const {clientId, projectId, projectCode, projectAlphaCode} = useSelector(
    (state: RootState) => state.reports
  );

  const onRemoveFilter = (filterId: number) => {
    setHasFilterValueChanged(true);
    setChipConfigs((prevConfig) =>
      prevConfig.map((filter) =>
        filter.id === filterId ? {...filter, filterSelection: []} : filter
      )
    );
  };

  const handleOnDropdownDataChanged = (
    id: number,
    filterData: ISelectedItems
  ) => {
    const updatedChipConfigs: AurChipConfig[] = [...chipConfigs];
    updatedChipConfigs[id].filterSelection = filterData?.map((item) => ({
      id: item.id,
      value: item.name,
    }));

    setChipConfigs(updatedChipConfigs);
    setHasFilterValueChanged(true);
  };

  const handleOnClearAll = () => {
    setClearAllModal(true);
  };

  const handleClearAllModalOptionSelected = () => {
    closeModalHandler();
    setChipConfigs((prevConfig) =>
      prevConfig.map((filter) => ({...filter, filterSelection: []}))
    );
  };

  const closeModalHandler = () => {
    setClearAllModal(false);
  };

  const indexRowExpansion = async (
    id: number,
    isExpanded: boolean,
    ecaIndexId: number,
    ecaNlpingestionId?: number
  ) => {
    setIsHistoryDetailLoading(true);
    try {
      const params = `?clientId=${clientId}&projectId=${projectId}`;
      let historyData: EcaIndexStatus[] = [];
      if (!isExpanded) {
        const result = await getIndexHistory(params, ecaIndexId);
        historyData = result.data.data
          .sort((a: EcaIndexStatus, b: EcaIndexStatus) => {
            const dateA = a.endDateTime ? new Date(a.endDateTime).getTime() : 0;
            const dateB = b.endDateTime ? new Date(b.endDateTime).getTime() : 0;
            return dateB - dateA;
          })
          .map((data: EcaIndexStatus) => {
            const randomId = generateRandomNumber();
            const normalizedData = normalizeDeleteDocumentsEvent(
              data,
              indexDetails.ele?.type === IndexesType.standard
            );
            return {
              ...normalizedData,
              id: randomId,
              type: INDEXING_SUBINDEX_CHILD,
              parentId: id,
              ecaNlpingestionId,
              showDownloadIcon: false,
            };
          });

        const firstCompleteWithErrors = historyData.find(
          (row: EcaIndexStatus) => isCompletedWithErrorStatus(row.status)
        );

        if (firstCompleteWithErrors) {
          firstCompleteWithErrors.showDownloadIcon = true;
        }
      }

      setRows((prevRows: EcaIndexStatus[]) => {
        const updatedRows = prevRows.map((parent: EcaIndexStatus) => {
          if (parent.id === id) {
            return {
              ...parent,
              isExpanded: !parent.isExpanded,
            };
          }
          return parent;
        });

        const resultData: EcaIndexStatus[] = [];
        updatedRows.forEach((parent: EcaIndexStatus) => {
          resultData.push(parent);

          if (parent.id === id && parent.isExpanded) {
            const randomId = generateRandomNumber();
            resultData.push({
              id: randomId,
              parentId: parent.id,
              name: INDEXING_SUBINDEX_EVENTS,
              type: INDEXING_SUB_INFO,
              isExpanded: false,
              showDownloadIcon: false,
              buildType: INDEXING_SUBINDEX_EVENTS,
            });
            resultData.push(...historyData);
          }
        });
        const collapsedParentIds = new Set(
          resultData
            .filter((row) => row.type === INDEXING_PARENT && !row.isExpanded)
            .map((row) => row.id)
        );

        const filteredResultData = resultData.filter(
          (row) => !collapsedParentIds.has(row.parentId)
        );

        return filteredResultData;
      });
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setIsHistoryDetailLoading(false);
    }
  };

  const filterTable = () => {
    const filteredParentRows = rows.filter((row: EcaIndexStatus) => {
      let matchesBuildType = true;
      let matchesStatus = true;
      let matchesInitiatedBy = true;

      if (chipConfigs[0].filterSelection.length > 0) {
        matchesBuildType = chipConfigs[0].filterSelection.some(
          (selected: FilterSelection) =>
            isDeleteDocumentsEvent({datasetName: selected.value}) &&
            isDeleteDocumentsEvent(row)
              ? true
              : row.datasetName === selected.value
        );
      }

      if (chipConfigs[1].filterSelection.length > 0) {
        matchesStatus = chipConfigs[1].filterSelection.some(
          (selected: FilterSelection) => {
            if (selected.value === t('indexingStatus.completedWithError')) {
              return isCompletedWithErrorStatus(row.status);
            } else if (selected.value === t('indexingStatus.inProgress')) {
              return indexingStatusKey.inProgress === row.status;
            } else {
              return row.status === selected.value;
            }
          }
        );
      }

      if (chipConfigs[2].filterSelection.length > 0) {
        matchesInitiatedBy = chipConfigs[2].filterSelection.some(
          (selected: FilterSelection) => row.initiatedByUser === selected.value
        );
      }

      return (
        row.type === INDEXING_PARENT &&
        matchesBuildType &&
        matchesStatus &&
        matchesInitiatedBy
      );
    });

    const parentMap = new Map<number | string, EcaIndexStatus>();
    filteredParentRows.forEach((parent) => {
      if (parent.id != null) {
        parentMap.set(parent.id, parent);
      }
    });

    const filteredChildRows = rows.filter((row: EcaIndexStatus) => {
      if (
        row.type === INDEXING_SUBINDEX_CHILD ||
        row.type === INDEXING_SUB_INFO
      ) {
        const parent = parentMap.get(row.parentId ?? '');
        return parent?.isExpanded === true;
      }
      return false;
    });

    const finalRows: EcaIndexStatus[] = [];

    filteredParentRows.forEach((parent: EcaIndexStatus) => {
      finalRows.push(parent);

      if (parent.isExpanded) {
        filteredChildRows.forEach((child: EcaIndexStatus) => {
          if (child.parentId === parent.id) {
            finalRows.push(child);
          }
        });
      }
    });
    return finalRows;
  };

  const fetchIndex = async () => {
    setIsHistoryDetailLoading(true);
    if (clientId && projectId) {
      try {
        const params = `?clientId=${clientId}&projectId=${projectId}`;
        const result = await getIndexByName(
          params,
          indexDetails.ele.fieldTemplateId
        );
        const responseData = result?.data?.data;
        responseData.sort((a: EcaIndexStatus, b: EcaIndexStatus) => {
          const dateA = a.startDateTime
            ? new Date(a.startDateTime).getTime()
            : 0;
          const dateB = b.startDateTime
            ? new Date(b.startDateTime).getTime()
            : 0;
          return dateB - dateA;
        });
        const indexData = responseData.map(
          (data: EcaIndexStatus, index: number) => {
            const normalizedData = normalizeDeleteDocumentsEvent(
              data,
              indexDetails.ele?.type === IndexesType.standard
            );
            return {
              ...normalizedData,
              id: index,
              type: INDEXING_PARENT,
              toggleExpansion: indexRowExpansion,
              showDownloadIcon: false,
              isExpanded: false,
            };
          }
        );

        const initiatedData = sortByName(
          result?.data?.data?.reduce(
            (acc: ISelectItem[], data: EcaIndexStatus, index: number) => {
              if (
                data.initiatedByUser &&
                !acc.some((item) => item.name === data.initiatedByUser)
              ) {
                acc.push({
                  name: data.initiatedByUser,
                  id: index,
                });
              }
              return acc;
            },
            []
          ) ?? []
        );

        indexData.forEach((row: EcaIndexStatus) => {
          if (
            isCompletedWithErrorStatus(row.status) &&
            !(
              row?.datasetName === datasetName.overlay ||
              row?.datasetName === datasetName.overlayMediaAPCUpdate ||
              row?.datasetName === datasetName.overlayProjectAPCUpdate
            )
          ) {
            row.showDownloadIcon = true;
          }
        });

        const rebuildStatus = indexData.find(
          (row: EcaIndexStatus) => row.status === indexingStatusKey.inProgress
        );
        if (rebuildStatus) {
          dispatch(setRebuildTrigger(true));
        } else {
          dispatch(setRebuildTrigger(false));
        }

        if (
          indexDetails.ele?.type === IndexesType.standard &&
          !skipApiBannerValidation
        ) {
          dispatch(
            setIndexingBannerData(
              getIndexingBannerFromStatusList(
                responseData,
                indexDetails.ele?.type
              )
            )
          );
        }

        setRows(indexData);
        setInitiatedList(initiatedData);
      } catch (e) {
        console.error('Error', e);
      } finally {
        setIsHistoryDetailLoading(false);
      }
    }
  };

  useEffect(() => {
    if (indexDetails.ele.type !== IndexesType.custom) {
      fetchIndex();
    }
  }, [clientId, projectId, skipApiBannerValidation]);

  const closeReRunModalHandler = () => {
    setVisibleReRunPopup(false);
    dispatch(
      setReRunPopup({showpopup: false, indexId: 0, overlayRetryData: undefined})
    );
    setTimeout(() => {
      setVisibleReRunPopup(true);
    }, 1000);
  };

  const retryNlpDeleteDocuments = async () => {
    try {
      const nlpProjectCode = projectAlphaCode || projectCode;
      if (!nlpProjectCode || !ecaNlpingestionId) {
        throw new Error('NLP retry requires project code and ingestion ID');
      }

      const result = await retryNlpDocuments(nlpProjectCode, ecaNlpingestionId);
      if (result.status >= 200 && result.status < 300) {
        fetchIndex();
      }
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(
        setReRunPopup({
          showpopup: false,
          indexId: 0,
          overlayRetryData: undefined,
        })
      );
      setIsHistoryDetailLoading(false);
      setTimeout(() => {
        setVisibleReRunPopup(true);
      }, 1000);
    }
  };

  const handleReRunContinue = () => {
    setVisibleReRunPopup(false);
    setIsHistoryDetailLoading(true);
    const shouldRetryNlpDeleteDocuments =
      Boolean(ecaNlpingestionId && ecaNlpingestionId > 0) &&
      isDeleteDocumentsEvent({datasetName: dataSetName});

    if (shouldRetryNlpDeleteDocuments) retryNlpDeleteDocuments();
    else if (dataSetName === datasetName.newExport) ReRunFailed();
    else overlayReRunFailed();
  };
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };
  useEffect(() => {
    if (showReRunPopup) {
      dispatch(
        setReRunPopup({
          showpopup: false,
          indexId: 0,
          overlayRetryData: undefined,
        })
      );
    }
  }, []);
  const reRunParams = `?clientId=${clientId}&projectId=${projectId}`;
  async function ReRunFailed() {
    try {
      const result = await reRunBuildHandler(reRunParams, reRunIndexId);
      const reRunStatus = result?.data?.meta?.status;
      if (reRunStatus == 'Success') {
        fetchIndex();
      }
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(
        setReRunPopup({
          showpopup: false,
          indexId: 0,
          overlayRetryData: undefined,
        })
      );
      setIsHistoryDetailLoading(false);
      setTimeout(() => {
        setVisibleReRunPopup(true);
      }, 1000);
    }
  }

  const overlayReRunFailed = async () => {
    try {
      let payload,
        path = '';
      switch (dataSetName) {
        case datasetName.overlay:
          payload = {
            datFilePath: datFilePath,
            retryIndexId: reRunIndexId,
            custodians: overlayRetryData?.custodians,
            fields: overlayRetryData?.fields,
          };
          path = 'overlay';
          break;
        case datasetName.overlayMediaAPCUpdate:
          payload = {
            mediaId,
            exportId,
          };
          path = 'mediaRefresh';
          break;
        case datasetName.overlayProjectAPCUpdate:
          path = 'ap';
          break;
      }

      const result = await retryOverlay(path, reRunParams, payload);
      const reRunStatus = result?.data?.meta?.status;
      if (reRunStatus == 'Success') {
        fetchIndex();
      }
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(
        setReRunPopup({
          showpopup: false,
          indexId: 0,
          overlayRetryData: undefined,
        })
      );
      setIsHistoryDetailLoading(false);
      setTimeout(() => {
        setVisibleReRunPopup(true);
      }, 1000);
    }
  };

  useEffect(() => {
    if (showToastMsg) {
      setOpenToast(true);
      setTimeout(() => {
        setOpenToast(true);
      }, 5000);

      setInterval(() => {
        setOpenToast(false);
        dispatch(setShowToast(false));
      }, 5000);
    }
  }, [showToastMsg]);

  const callGATrackingEvent = (filterName: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.FILTER,
      click: {
        name: filterName,
        type: GA_CLICK_TYPES.DROPDOWN,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.indexManagement.filteredStandardIndexList'),
      },
    });
  };

  const filteredRows = useMemo(() => filterTable(), [rows, chipConfigs]);
  const columns = useMemo(() => getIndexColumns(), []);

  const rowMap = useMemo(
    () => new Map(filteredRows.map((row) => [row.id, row])),
    [filteredRows]
  );

  const getRowHeight = useCallback(
    (params: GridRowHeightParams) => {
      const row = rowMap.get(params.id);

      return row?.type === INDEXING_SUB_INFO
        ? INDEXING_SUBINDEX_HEIGHT
        : ROW_HEIGHT;
    },
    [rowMap]
  );

  return (
    <div className="index-filter-dropdown">
      <Grid container gap={1} className="index-dropdown-container">
        {/* Last Accessed Date Dropdown */}
        <Grid item xs={6} md={3}>
          <AurSelectDropdown
            items={buildTypeList()}
            searchPlaceHolder={t('userHomePage.search')}
            itemsCountwithNoScroll={3}
            itemDisplay="name"
            itemKey="id"
            selectAllLabel={t('common.selectAll')}
            dropdownLabel={t('indexes.buildType')}
            onSelectionChange={(data) => {
              if (!data) return;
              const transformedData: ISelectedItems = data.map((item) => ({
                id: item.id as number,
                name: item.name as string,
              }));
              callGATrackingEvent(
                t('gaTracking.indexManagement.filters.buildType')
              );
              handleOnDropdownDataChanged(0, transformedData);
            }}
            defaultSelection={chipConfigs?.[0]?.filterSelection?.map((item) => {
              return {
                id: item.id,
                name: item.value,
              };
            })}
            hasValidationError={false}
            validationText=""
            helperText=""
            aria-label={t('filters.buildType')}
            clearValues={chipConfigs?.[0]?.filterSelection.length === 0}
            onClearClicked={() => onRemoveFilter(1)}
            data-testId="aur-test-user-dropdown"
            hintText={t('indexes.filterHint')}
            search={false}
            disabled={rows.length === 0}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <AurSelectDropdown
            items={statusList()}
            searchPlaceHolder={t('userHomePage.search')}
            itemsCountwithNoScroll={3}
            itemDisplay="name"
            itemKey="id"
            selectAllLabel={t('common.selectAll')}
            dropdownLabel={t('indexes.status')}
            onSelectionChange={(data) => {
              if (!data) return;

              const transformedData: ISelectedItems = data.map((item) => ({
                id: item.id as number,
                name: item.name as string,
              }));
              callGATrackingEvent(
                t('gaTracking.indexManagement.filters.status')
              );
              handleOnDropdownDataChanged(1, transformedData);
            }}
            defaultSelection={chipConfigs?.[1]?.filterSelection?.map((item) => {
              return {
                id: item.id,
                name: item.value,
              };
            })}
            hasValidationError={false}
            validationText=""
            helperText=""
            aria-label={t('indexes.status')}
            disabled={rows.length === 0}
            clearValues={chipConfigs?.[1]?.filterSelection.length === 0}
            onClearClicked={() => onRemoveFilter(2)}
            search={false}
            hintText={t('indexes.filterHint')}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <AurSelectDropdown
            items={initiatedList}
            searchPlaceHolder={t('userHomePage.search')}
            itemsCountwithNoScroll={3}
            itemDisplay="name"
            itemKey="id"
            selectAllLabel={t('common.selectAll')}
            dropdownLabel={t('indexes.initiatedBy')}
            onSelectionChange={(data) => {
              if (!data) return;

              const transformedData: ISelectedItems = data.map((item) => ({
                id: item.id as number,
                name: item.name as string,
              }));
              callGATrackingEvent(
                t('gaTracking.indexManagement.filters.initiatedBy')
              );
              handleOnDropdownDataChanged(2, transformedData);
            }}
            defaultSelection={chipConfigs?.[2]?.filterSelection?.map((item) => {
              return {
                id: item.id,
                name: item.value,
              };
            })}
            disabled={initiatedList?.length === 0}
            hasValidationError={false}
            validationText=""
            helperText=""
            aria-label={t('indexes.initiatedBy')}
            clearValues={chipConfigs?.[2]?.filterSelection.length === 0}
            onClearClicked={() => onRemoveFilter(3)}
            search={initiatedList?.length > 11}
            hintText={t('indexes.filterHint')}
          />
        </Grid>
      </Grid>
      {(chipConfigs[0]?.filterSelection.length > 0 ||
        chipConfigs[1]?.filterSelection.length > 0 ||
        chipConfigs[2]?.filterSelection.length > 0) && (
        <div className="report-chip-container">
          <AurChipList
            onClearAll={handleOnClearAll}
            hasFilterValueChanged={hasFilterValueChanged}
            chipConfigs={chipConfigs}
            saveButtonShow={false}
          />
        </div>
      )}

      {clearAllModal && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={clearAllModal}
          onOk={handleClearAllModalOptionSelected}
          onCancel={closeModalHandler}
          popupTitle={t('common.justToConfirm')}
          popupHeading={t('common.clearAllPopup.clearHeading')}
          popupContent={t('common.clearAllPopup.popupContent')}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={true}
          labelActionCancel={t('common.clearAllPopup.buttonCancel')}
          labelActionOk={t('common.clearAllPopup.buttonYes')}
          exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
          continueButtonHint={t('common.clearAllPopup.confirmToClearHint')}
          cancelButtonTitle=""
        />
      )}
      <div className="index-table">
        <AurDataGridPro
          className="reports-grid"
          cellClassName="cell-truncate"
          rows={filteredRows}
          getRowId={(row) => row.id}
          getRowHeight={getRowHeight}
          columns={columns}
          checkBoxVisible={false}
          entityType={INDEXING_HISTORY}
          pointer={true}
          getTreeDataPath={(row) => row.path}
          getRowClassName={(params) => {
            switch (params.row.type) {
              case 'info':
                return 'sub-index-row index-sub-header-row';
              case 'child':
                return 'index-child-row';
              default:
                return '';
            }
          }}
          rowSelection={false}
          aria-label={t('accessibility.table.hostingUserSummary')}
          enableRowClickOnEnterPress
          noRowsOverlay={AurDataGridLoadingOverlay}
          hideFooterSelectedRowCount={true}
          paginationMode="server"
          pageSizeOptions={[rows.length]}
          showPagination={false}
          defaultItemsPerPage={rows.length}
          hideFooterPagination={true}
        />
        <div className="aur-toast-download">
          <AurToast
            hideDuration={5000}
            open={openToast}
            severity={'error'}
            message={t('indexes.downloadFailed')}
            onClose={() => handleSnackbarClose}
            horizontalPosition={'center'}
          />
        </div>
      </div>
      {<AurLoadingIndicator overlay={true} isOpen={isHistoryDetailLoading} />}
      {showReRunPopup && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={visibleReRunPopup}
          onOk={handleReRunContinue}
          onCancel={closeReRunModalHandler}
          showPopupHeading={true}
          popupHeading={t('common.clearAllPopup.heading')}
          popupTitle={t(`indexes.reRunPopup.title`)}
          popupContent={ReRunPopContent()}
          contentType="element"
          showPopupTitle={true}
          showCloseIcon={false}
          labelActionCancel={t('common.clearAllPopup.buttonCancel')}
          labelActionOk={t('indexes.reRunPopup.continueBtn')}
          exitButtonHint={t('indexes.reRunPopup.cancelButtonHint')}
          continueButtonHint={t('indexes.reRunPopup.continueButtonHint')}
          cancelButtonTitle=""
          titleCustomClass="label2_size"
        />
      )}
    </div>
  );
};
export default IndexingFilter;
